import type {
  BUTTON_TYPE_CATALOG,
  BUTTON_TYPE_FLOW,
  BUTTON_TYPE_MPM,
  BUTTON_TYPE_SPM,
  HEADER_FORMATS,
} from "@dashboardApi/apis/whatsappMessageTemplates/constants";

interface WhatsAppTemplateQuickReplyButton {
  type: "QUICK_REPLY";
  text: string;
}

interface WhatsAppTemplateUrlButton {
  type: "URL";
  url: string;
  text: string;
}

interface WhatsAppTemplatePhoneNumberButton {
  type: "PHONE_NUMBER";

  phone_number: string;
  text: string;
}

interface WhatsAppTemplateCopyCodeButton {
  type: "COPY_CODE";
  text: string;
  offer_code: string;
}

interface WhatsAppTemplateCatalogButton {
  type: typeof BUTTON_TYPE_CATALOG;
  text: string;
}

interface WhatsAppTemplateMultiProductButton {
  type: typeof BUTTON_TYPE_MPM;
  text: string;
}

interface WhatsAppTemplateSingleProductButton {
  type: typeof BUTTON_TYPE_SPM;
  text: string;
}

interface WhatsAppTemplateFlowButton {
  type: typeof BUTTON_TYPE_FLOW;
  text: string;
  // Note: This reflects the field as returned by the Meta Templates API
  // We convert this to a string when mapping the Meta response onto our own types (EditableButton), since flow ID is supposed to be a string
  flow_id: number;
  // Undefined if template uses the default icon
  icon?: WhatsAppIconType;
}

// If updating, also update hasOnlySupportedButtons
type WhatsAppTemplateButton =
  | WhatsAppTemplateQuickReplyButton
  | WhatsAppTemplateUrlButton
  | WhatsAppTemplatePhoneNumberButton
  | WhatsAppTemplateCopyCodeButton
  | WhatsAppTemplateCatalogButton
  | WhatsAppTemplateMultiProductButton
  | WhatsAppTemplateSingleProductButton
  | WhatsAppTemplateFlowButton;

// WhatsApp Flow templates can have a custom icon configured in the WhatsApp Manager
export type WhatsAppIconType = "REVIEW" | "PROMOTION" | "DOCUMENT";

export type WhatsAppButtonType =
  | "QUICK_REPLY"
  | "URL"
  | "PHONE_NUMBER"
  | "COPY_CODE"
  | "LIST"
  | typeof BUTTON_TYPE_CATALOG
  | typeof BUTTON_TYPE_MPM
  | typeof BUTTON_TYPE_SPM
  | typeof BUTTON_TYPE_FLOW
  | "UNTYPED";

export interface HeaderComponent {
  type: "HEADER";
  text?: string;
  format:
    | typeof HEADER_FORMATS.TEXT
    | typeof HEADER_FORMATS.IMAGE
    | typeof HEADER_FORMATS.VIDEO
    | typeof HEADER_FORMATS.DOCUMENT
    | typeof HEADER_FORMATS.LOCATION;
}

export interface TextComponent {
  type: "BODY" | "FOOTER";
  text: string;
}

export interface ButtonsComponent {
  type: "BUTTONS";
  buttons: WhatsAppTemplateButton[];
}

export interface CarouselCard {
  components: Exclude<WhatsAppMessageTemplateComponent, CarouselComponent>[];
}

export interface CarouselComponent {
  type: "CAROUSEL";
  cards: CarouselCard[];
}

export interface LimitedTimeOfferComponent {
  type: "LIMITED_TIME_OFFER";
  limited_time_offer: {
    text: string;
    has_expiration: boolean;
  };
}

export type WhatsAppMessageTemplateComponent =
  | TextComponent
  | ButtonsComponent
  | HeaderComponent
  | CarouselComponent
  | LimitedTimeOfferComponent;

export function isButtonsComponent(c: WhatsAppMessageTemplateComponent): c is ButtonsComponent {
  return (c as ButtonsComponent).buttons !== undefined;
}

export function isCarouselComponent(c: WhatsAppMessageTemplateComponent): c is CarouselComponent {
  return c.type === "CAROUSEL" && c.cards.length > 0;
}

export interface WhatsAppMessageTemplate {
  name: string;
  components: WhatsAppMessageTemplateComponent[];
  language: string;
  status: string;
  category: string;
  id: string;
  is_draft?: boolean;

  last_updated_time?: string;
}
